import {
  HStack,
  Popover,
  PopoverTrigger,
  Portal,
  PopoverContent,
  PopoverHeader,
  PopoverCloseButton,
  PopoverBody,
  Spacer,
  Stack,
  NumberInput,
  theme,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  PopoverFooter,
  Button,
  ButtonGroup,
  Text,
  Box,
} from '@chakra-ui/core';
import React from 'react';
import { SchemaBuilderProps } from './formBuilderSchema';
import { MdKeyboardArrowDown } from 'react-icons/md';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
interface MinMaxSelectorProps {
  minValue: number;
  maxValue: number;
  onChange: (value: { min: number; max: number }) => void;
}

const MinMaxSelector = React.memo(
  function ({ minValue = 0, maxValue = 10, onChange }: MinMaxSelectorProps) {
    const { t } = useTranslation('hrFormTemplate');

    const [max, setMax] = React.useState(maxValue);
    const [min, setMin] = React.useState(minValue);

    const hasValuesChanged = React.useMemo(() => {
      return max !== maxValue || min !== minValue;
      // eslint-disable-next-line
    }, [max, min]);

    React.useEffect(() => {
      setMin(minValue);
      // eslint-disable-next-line
    }, [minValue]);

    React.useEffect(() => {
      setMax(maxValue);
      // eslint-disable-next-line
    }, [, maxValue]);

    function handleClose(onClose: () => void) {
      onClose();
    }

    function handleSave(onClose: () => void) {
      onChange({ min, max });
      onClose();
    }

    return (
      <HStack>
        <Popover placement="bottom" closeOnBlur={false}>
          {({ onClose }) => (
            <>
              <PopoverTrigger>
                <Box backgroundColor="#E2E8F0" paddingX={theme.space[2]} paddingY={theme.space[1]} borderRadius={theme.radii.md}>
                  <HStack>
                    <Text fontSize="sm" cursor="pointer">
                      {t('formBuilder.characterLimit.title')} ({minValue} - {maxValue})
                    </Text>
                    <MdKeyboardArrowDown size={20} />
                  </HStack>
                </Box>
              </PopoverTrigger>
              <Portal>
                <PopoverContent>
                  <PopoverHeader pt={4} fontWeight="bold" border="0">
                    {t('formBuilder.characterLimit.title')}
                  </PopoverHeader>

                  <PopoverCloseButton disabled={hasValuesChanged} />
                  <PopoverBody>
                    <Text fontSize="sm">{t('formBuilder.characterLimit.message', { min, max })}</Text>
                    <Spacer marginY={3} />
                    <HStack>
                      <Stack flexShrink={0} flexGrow={1} spacing={1}>
                        <Text fontSize="xs" fontWeight="bold" whiteSpace="nowrap">
                          {t('formBuilder.characterLimit.min')}:
                        </Text>
                        <NumberInput
                          allowMouseWheel
                          step={10}
                          value={min || 0}
                          min={0}
                          max={max || 1000}
                          onChange={(e) => setMin(Number(e))}
                          borderRadius={theme.radii.md}
                          size="sm"
                          maxW={'8rem'}
                        >
                          <NumberInputField borderRadius={theme.radii.md} />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </Stack>

                      <Stack flexShrink={0} flexGrow={1} spacing={1}>
                        <Text fontSize="xs" fontWeight="bold" whiteSpace="nowrap">
                          {t('formBuilder.characterLimit.max')}:
                        </Text>
                        <NumberInput
                          allowMouseWheel
                          step={10}
                          value={max || 0}
                          min={min || 0}
                          max={1000}
                          maxW={'8rem'}
                          onChange={(e) => setMax(Number(e))}
                          borderRadius={theme.radii.md}
                          size="sm"
                        >
                          <NumberInputField borderRadius={theme.radii.md} />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </Stack>
                    </HStack>
                    <Spacer marginTop={3} />
                  </PopoverBody>
                  <PopoverFooter d="flex" alignItems="center" justifyContent="space-between" pb={4}>
                    <Button
                      colorScheme="gray"
                      variant="ghost"
                      onClick={() => {
                        setMax(maxValue);
                        setMin(minValue);
                      }}
                      disabled={!hasValuesChanged}
                      size="sm"
                    >
                      {t('formBuilder.characterLimit.reset')}
                    </Button>
                    <ButtonGroup size="sm">
                      <Button colorScheme="gray" variant="outline" onClick={() => handleClose(onClose)}>
                        {t('formBuilder.characterLimit.cancel')}
                      </Button>
                      <Button colorScheme="blue" onClick={() => handleSave(onClose)} disabled={!hasValuesChanged}>
                        {t('formBuilder.characterLimit.done')}
                      </Button>
                    </ButtonGroup>
                  </PopoverFooter>
                </PopoverContent>
              </Portal>
            </>
          )}
        </Popover>
      </HStack>
    );
  },
  (prev, next) => prev.minValue === next.minValue && prev.maxValue === next.maxValue && prev.onChange === next.onChange,
);

const InputMinMaxSelector = React.memo(
  ({ defaultValue, onChange }: Pick<SchemaBuilderProps, 'defaultValue' | 'onChange'>) => {
    if (!defaultValue || defaultValue?.previewPlaceholders?.disableMinMax) {
      return null;
    }
    function handleOnChange(val: { min: number; max: number }) {
      const newDate = { ...defaultValue, ...val };
      onChange(newDate);
    }
    return <MinMaxSelector minValue={defaultValue.min!} maxValue={defaultValue.max!} onChange={handleOnChange} />;
  },
  (prev, next) => isEqual(prev.defaultValue, next.defaultValue) && prev.onChange === next.onChange,
);
export default InputMinMaxSelector;
